import { isNilOrEmpty, isNotNilOrEmpty, mapIndexed, notEqual } from 'ramda-adjunct';
import React, { useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useHistory } from 'react-router-dom';
import { all, always, andThen, any, anyPass, assoc, assocPath, compose, dissoc, equals, filter, flatten, has, head, ifElse, isEmpty, isNil, length, lensPath, lensProp, map, none, over, path, pathOr, prop, propEq, propOr, propSatisfies, reduce, uniq, when, isNotNil, isNotEmpty } from 'ramda';
import { withEditing } from '../pages/docs/components/helpers/widget';
import { uploadFiles } from '../lib/uploadFiles';
import { getElementByVersion, getFlexigetElements } from '../pages/docs/components/helpers/flexigets';
import { customSuccessMessage, errorMessage, successMessage } from '../utils/messageMutation';
import { BASE_FRAME_STYLE, SCOPE_OFFER } from '../_CONST';
import CreateWidgetMutation from '../_graphql/mutations/documents/CreateWidgetMutation';
import DeleteWidgetMutation from '../_graphql/mutations/documents/DeleteWidgetMutation';
import UpdateWidgetMutation from '../_graphql/mutations/documents/UpdateWidgetMutation';
import UpdateCacheMutation from '../_graphql/mutations/UpdateCacheMutation';
import { getVariablesOnWidgetHtml } from '../pages/helpers/getVariablesOnWidgetHtml';
import { ACTIONS, TARGET_TYPES, buildEntry, hasWidgetContentChanged, buildWidgetPlacements } from '../lib/sessionHistory';
import useRules from './useRules';

const NB_COLS_GRID = 100;
const HEIGHT_ROWS_GRID = 5;

const useWidget = ({
  widget = {},
  scope,
  offerId,
  onOfferRedirect,
  flexigets,
  setWidgetCreated,
  variablesForRules,
  globalVariables,
  origin,
  pages,
  documentId
}) => {
  const widgetExist = isNotNil(prop('id', widget));
  const [widgetId, setWidgetId] = useState(null);
  const [elements, setElements] = useState(getFlexigetElements(flexigets));
  const [isEdit, setIsEdit] = useState(false);
  const [isOpenCreateGlobalVariableModal, setIsOpenCreateGlobalVariableModal] = useState(false);
  const [state, setState] = useState({
    values: setDefaultValues(widget, {
      scope,
      offerId
    }),
    loading: false,
    deleteLoading: false,
    isEditing: false
  });

  const widgetValues = setDefaultValues(widget, {
    scope,
    offerId
  });
  const { getConditionsAreValid } = useRules({});
  const prevContents = useRef({});
  // Baseline for the widget-edit diff (NAME_UPDATE / CONTENT_UPDATE). It must reflect
  // the widget AS LOADED, not the first render: in EditWidgetModal the widget is fetched
  // asynchronously, so the first render sees an empty {} and capturing that here would
  // make every save look like a content change (spurious CONTENT_UPDATE on a rename-only
  // edit). We (re)capture it the first time a real widget (with an id) is present.
  const initialSnapshotRef = useRef(setDefaultValues(widget, { scope, offerId }));
  const initialSnapshotCapturedRef = useRef(widgetExist);
  const history = useHistory();

  useEffect(() => {
    if (widgetExist && !initialSnapshotCapturedRef.current) {
      initialSnapshotRef.current = setDefaultValues(widget, { scope, offerId });
      initialSnapshotCapturedRef.current = true;
    }
  }, [widget, widgetExist, scope, offerId]);

  useEffect(() => {
    const contents = path(['values', 'contents'], state);
    if (notEqual(prevContents.current, contents)) {
      map((element) => {
        const Component = getElementByVersion(prop('type', element), propOr('1.0', 'version', element)).Component;
        setElements(assoc(prop('componentName', Component), Component, elements));
      })(contents);
      prevContents.current = contents;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path(['values', 'contents'], state)]);

  useEffect(() => {
    if (widgetExist) {
      setState(assoc('values', widgetValues));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget, scope, offerId, widgetExist]);

  useEffect(() => {
    const signatureElements = filter(propEq('signature', 'type'))(path(['values', 'contents'], state));
    if (length(signatureElements) > 0) {
      mapIndexed((e, id) => {
        setState((prevState) =>
          assocPath(['values', 'contents', id, 'type'], 'text', prevState));
      })(signatureElements);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path(['values', 'contents'], state)]);

  const variablesOnElements = getVariablesOnWidgetHtml(path(['values', 'contents'], state), globalVariables);

  const redirect = () => history.push(onOfferRedirect());

  const variablesUsedOnRules = compose(
    uniq,
    filter(isNotNil),
    map(prop('variable')),
    flatten,
    map(path(['rule', 'conditions'])),
    filter((element) => element.rule && isNotNilOrEmpty(path(['rule', 'conditions'], element)))
  )(path(['values', 'contents'], state));
  const hasVariableOnRulesNotInVariables = any((variable) => none((v) => equals(prop('key', v), variable), variablesForRules))(variablesUsedOnRules);

  const onSave = async (reFetch, saveAndRedirect) => {
    setState(assoc('loading', true));

    const removeUnusedVariablesOnRule = (rule) => {
      const conditions = propOr([], 'conditions', rule);
      const validConditions = filter(
        (condition) => any((v) => equals(prop('key', v), prop('variable', condition)), variablesForRules),
        conditions
      );
      if (notEqual(conditions, validConditions)) {
        return assoc('conditions', validConditions, rule);
      }
      return rule;
    };

    const cleanValues = (state) => compose(
      over(
        lensProp('contents'),
        map(over(
          lensProp('rule'),
          (rule) => {
            if (hasVariableOnRulesNotInVariables) rule = removeUnusedVariablesOnRule(rule);
            return isNotNilOrEmpty(propOr([], 'conditions', rule)) ? rule : null;
          }
        ))
      ),
      propOr({}, 'values')
    )(state);

    const processImages = async (values) => {
      const hasImage = compose(
        any(propEq('image', 'type')),
        prop('contents')
      )(values);

      if (hasImage) {
        const contents = await saveImages(values);
        if (any(isNil)(contents)) {
          setState(assoc('loading', false));
          return null;
        }
        return { ...values, contents };
      }
      return values;
    };

    const updateHtmlContent = (values) => {
      when(
        (elementsFlexiget) => equals(length(path(['contents'], values)), length(elementsFlexiget)),
        map((element, key) => {
          if (equals('childrens', prop('type', element))) element.type = 'children';

          const ElementFlexiget = getElementByVersion(prop('type', element), propOr('1.0', 'version', element)).Component;
          const html = path(['element', 0, 'html'], element);
          const elementGetHtml = isNotNilOrEmpty(html);

          const content = elementGetHtml ? html : renderToStaticMarkup(<ElementFlexiget value={prop('element', element)} key={key} />);
          element.htmlContent = content;

          const elementContentSlate = compose(
            map(
              compose(
                dissoc('children'),
                when(
                  (c) => anyPass([equals('paragraph'), equals('signature'), equals('checkbox')])(prop('type', c)),
                  assoc('type', 'text')
                )
              )
            ),
            filter(has('children'))
          )(prop('element', element));
          if (isNotEmpty(elementContentSlate)) element.element = elementContentSlate;
        })
      )(prop('contents', values));

      return compose(
        over(
          lensProp('contents'),
          JSON.stringify
        )
      )(values);
    };

    const cleanedValues = cleanValues(state);
    // Compare like-for-like: cleanValues normalizes each element's `rule` (empty → null),
    // so the baseline snapshot must be cleaned the same way, otherwise a rename-only edit
    // is misreported as a content change (spurious CONTENT_UPDATE).
    const cleanedInitialSnapshot = cleanValues({ values: initialSnapshotRef.current });
    const widgetHistoryEntries = (widgetExist || isEdit)
      ? computeWidgetHistoryEntries(cleanedInitialSnapshot, cleanedValues, { origin, pages })
      : [];

    let values = await compose(
      andThen(updateHtmlContent),
      async (values) => processImages(values),
      cleanValues
    )(state);

    const handleWidgetSaveCallback = successMessage => (ok, error, widgetId) => {
      setWidgetId(widgetId);
      setState(assoc('loading', false));

      if (ok && !error) {
        customSuccessMessage(successMessage);
        const newName = prop('name', values);
        const nameHasChanged = notEqual(prop('name', widget), newName);
        const shouldUpdateCache = equals(SCOPE_OFFER, scope) && (nameHasChanged || !(widgetExist || isEdit));

        if (shouldUpdateCache) {
          UpdateCacheMutation({ date: new Date(), key: `widgets-${offerId}` }, () => {});
        }
        if (setWidgetCreated) {
          setWidgetCreated({
            ...values, id: widgetId, dates: { creation: new Date() }
          });
        }
        if (reFetch) {
          history.push(`/offre/${offerId}/widgets/${widgetId}/editer`);
        }
        if (saveAndRedirect) redirect();
      } else {
        errorMessage();
      }
    };

    if (widgetExist || isEdit) {
      const setId = widgetId || prop('id', values);
      UpdateWidgetMutation({
        widget: { ...values, id: setId }, documentId, historyEntries: widgetHistoryEntries 
      }, handleWidgetSaveCallback('Le widget a bien été mis à jour'));
      setIsEdit(true);
    } else {
      values = dissoc('id', values);
      CreateWidgetMutation({ widget: values }, handleWidgetSaveCallback('Le widget a bien été créé.'));
    }

    setState(assoc('isEditing', false));
    return true;
  };

  const checkRuleAndSave = (refetch, saveAndRedirect) => async () => {
    const allAreValid = all(compose(
      ifElse(isNil, always(true), getConditionsAreValid),
      pathOr(null, ['rule', 'conditions'])
    ))(path(['values', 'contents'], state));

    if (allAreValid) {
      return await onSave(refetch, saveAndRedirect);
    } else {
      errorMessage('Veuillez vérifier la règle de chaque élément.');
    }
  };

  const onDelete = () => {
    DeleteWidgetMutation({ widgetId: prop('id', widget) }, (ok, error) => {
      if (ok && !error) {
        successMessage('widget', 'supprimé');
        if (equals(SCOPE_OFFER, scope)) {
          UpdateCacheMutation({ date: new Date(), key: `widgets-${offerId}` }, () => {});
        }
        redirect();
      } else {
        errorMessage();
      }
    });
  };

  const isValid = propsAreDefined(['name', 'scope', 'contents'])(prop('values', state));

  const updateRule = (index) => (fn) => {
    const newState = compose(
      withEditing,
      over(lensPath(['values', 'contents', index, 'rule']), fn)
    )(state);
    setState(newState);
    return newState;
  };

  const removeRule = (index) => () => updateRule(index)(always(null));

  const selectVariable = (elementIndex, conditionIndex, variable) =>
    setState((prevState) =>
      compose(
        withEditing,
        assocPath(['values', 'contents', elementIndex, 'rule', 'conditions', conditionIndex, 'variable'], variable)
      )(prevState));

  return {
    ...state,
    setState,
    onSave: checkRuleAndSave,
    redirect,
    isValid,
    isEdit,
    onDelete,
    variablesOnElements,
    setIsEdit,
    widgetExist,
    isUsed: propOr(false, 'isUsed', widget),
    isUsedBy: propOr([], 'isUsedBy', widget),
    elements,
    getConditionsAreValid,
    updateRule,
    removeRule,
    isOpenCreateGlobalVariableModal,
    setIsOpenCreateGlobalVariableModal,
    selectVariable,
    hasVariableOnRulesNotInVariables
  };
};

const setDefaultValues = (widget, {
  scope,
  offerId
}) => ({
  id: propOr(undefined, 'id', widget),
  name: propOr('', 'name', widget),
  nbColsGrid: propOr(NB_COLS_GRID, 'nbColsGrid', widget),
  heightRowsGrid: propOr(HEIGHT_ROWS_GRID, 'heightRowsGrid', widget),
  contents: JSON.parse(propOr('[]', 'contents', widget)),
  scope,
  offerId,
  orientation: propOr('portrait', 'orientation', widget),
  frameStyle: propOr(BASE_FRAME_STYLE, 'frameStyle', widget)
});

export const computeWidgetHistoryEntries = (initialSnapshot, finalValues, context = {}) => {
  const { origin = null, pages = [] } = context;
  const widgetId = propOr(null, 'id', finalValues);
  const originSectionIndex = origin ? origin.sectionIndex : null;
  const originBlock = origin ? origin.block : null;
  const placements = buildWidgetPlacements(pages, widgetId, originSectionIndex, originBlock);

  const common = {
    targetType: TARGET_TYPES.WIDGET,
    widgetId,
    sectionIndex: originSectionIndex,
    block: originBlock,
    placements
  };

  const entries = [];
  const previousName = propOr('', 'name', initialSnapshot);
  const nextName = propOr('', 'name', finalValues);
  if (notEqual(previousName, nextName)) {
    entries.push(buildEntry({
      ...common,
      action: ACTIONS.NAME_UPDATE,
      targetName: nextName,
      previousName
    }));
  }
  if (hasWidgetContentChanged(initialSnapshot, finalValues)) {
    entries.push(buildEntry({
      ...common,
      action: ACTIONS.CONTENT_UPDATE,
      targetName: nextName
    }));
  }
  // This path bypasses the session stack (useSessionHistory), so assign the creation
  // order (seq) here by push order. The back turns seq into a ms offset on savedAt,
  return entries.map((entry, seq) => ({ ...entry, seq }));
};

const propsAreDefined = (props) => (object) => reduce((acc, prop) => {
  return acc && !propSatisfies(isNilOrEmpty, prop)(object);
}, true)(props);

export const saveImages = async ({ contents }) => {
  return await Promise.all(contents.map(async (content) => {
    const {
      type,
      element
    } = content;
    if (type === 'image' && isNil(path([0, 'fileId'], element))) {
      const uploadImage = compose(propOr({}, 'uploadImage'), head)(element);
      if (isEmpty(uploadImage)) {
        errorMessage('Pour sauvegarder le widget, veuillez ajouter une image.');
        return;
      } else {
        const { fileId } = await uploadFiles(uploadImage);
        const imageSrc = `/files/${fileId}`;
        const newElement = compose(
          assoc('fileId', fileId),
          assoc('imageSrc', imageSrc)
        )(head(element));
        content = {
          ...content,
          element: [newElement]
        };
      }
    }
    return content;
  }));
};

export default useWidget;
